/**
 * Recherche globale de l'en-tête.
 *
 * Le bouton « Rechercher une entreprise… » se contentait de renvoyer vers la
 * liste des entreprises : il en avait l'apparence sans la fonction. Il ouvre
 * désormais une palette qui interroge l'API à la frappe et mène directement à
 * une fiche — le chemin habituel quand on cherche une société précise, et non
 * une liste à filtrer.
 *
 * Accessible aussi au clavier par ⌘K / Ctrl+K (voir CrmLayout).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Building2, CornerDownLeft, ListFilter } from "lucide-react";
import api from "@/lib/crmApi";
import { StatusBadge, ScoreBadge } from "@/crm/ui";
import { ClientChip } from "@/crm/components/ClientControls";
import Modal from "@/crm/components/Modal";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const LIMIT = 8;

export default function GlobalSearch({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef(null);
  const abort = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setItems([]); setActive(0); return; }
    // Le focus doit attendre le montage réel du champ.
    const t = setTimeout(() => input.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (!open || term.length < MIN_CHARS) { setItems([]); setLoading(false); return; }
    const timer = setTimeout(async () => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      setLoading(true);
      try {
        // `client: "all"` : la palette cherche dans TOUT le CRM. Les clients
        // sont retirés des listes de prospection (crm/clients.py) ; les rendre
        // introuvables à la recherche serait une autre affaire — on ouvre
        // justement leur fiche pour voir qu'ils sont clients.
        const { data } = await api.get("/companies", {
          params: { search: term, limit: LIMIT, client: "all" }, signal: ctrl.signal,
        });
        setItems(data.items || []);
        setActive(0);
      } catch {
        if (!ctrl.signal.aborted) setItems([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q, open]);

  const openCompany = useCallback((c) => { onClose(); navigate(`/entreprises/${c.id}`); }, [navigate, onClose]);
  const openList = useCallback(() => {
    onClose();
    navigate(`/entreprises?search=${encodeURIComponent(q.trim())}`);
  }, [navigate, onClose, q]);

  if (!open) return null;

  const term = q.trim();
  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (items.length ? (i + 1) % items.length : 0)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (items.length ? (i <= 0 ? items.length : i) - 1 : 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (items[active]) openCompany(items[active]);
      else if (term.length >= MIN_CHARS) openList();
    }
  };

  return (
    <Modal onClose={onClose} align="top" padded={false} label="Recherche globale"
      className="max-w-[560px] rounded-2xl border-0 shadow-[0_30px_60px_-20px_rgba(10,12,24,.45)]"
      overlayClassName="[&>div:first-child]:bg-[rgba(11,21,36,.45)] [&>div:first-child]:backdrop-blur-[3px]"
      testid="global-search-panel" overlayTestid="global-search-overlay">
      <div className="flex items-center gap-2.5 border-b border-[#ebebeb] px-3.5">
        <Search size={17} className="shrink-0 text-[#94a3b8]" />
        <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
          data-testid="global-search-input" aria-label="Rechercher une entreprise"
          placeholder="Nom, SIREN, SIRET, ville…"
          className="h-[52px] min-w-0 flex-1 text-[15px] text-[#0f172a] outline-none placeholder:text-[#94a3b8]" />
        {loading && <Loader2 size={15} className="shrink-0 animate-spin text-[#cbd5e1]" />}
        <button type="button" onClick={onClose} data-testid="global-search-close" aria-label="Fermer la recherche"
          className="h-[30px] shrink-0 rounded-lg border border-[#e3e3e3] bg-[#fafafa] px-2 font-mono text-[11px] text-[#64748b] transition-colors hover:bg-[#f2f4f7]">
          Échap
        </button>
      </div>

      <div className="max-h-[52vh] overflow-y-auto p-2">
        {term.length < MIN_CHARS ? (
          <p className="px-4 py-6 text-center text-[13px] text-[#94a3b8]">
            Tapez au moins {MIN_CHARS} caractères pour chercher une entreprise.
          </p>
        ) : !loading && items.length === 0 ? (
          <p className="px-4 py-6 text-center text-[13px] text-[#94a3b8]" data-testid="global-search-empty">
            Aucune entreprise ne correspond à « {term} ».
          </p>
        ) : (
          items.map((c, i) => (
            <button key={c.id} data-testid="global-search-result"
              onMouseEnter={() => setActive(i)} onClick={() => openCompany(c)}
              className={`flex min-h-[52px] w-full items-center gap-[11px] rounded-[11px] px-2.5 text-left transition-colors ${i === active ? "bg-[#f5f9ff]" : "hover:bg-[#f5f9ff]"}`}>
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[#f2f4f7] text-[#64748b]">
                <Building2 size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-[#0f172a]">{c.nom}</span>
                <span className="block truncate text-xs text-[#94a3b8]">
                  {[c.ville, c.code_postal, c.libelle_naf || c.naf].filter(Boolean).join(" · ") || "—"}
                </span>
              </span>
              <ClientChip company={c} />
              {c.status && <StatusBadge status={c.status} />}
              {c.lead_score != null && <ScoreBadge score={c.lead_score} category={c.score_category} />}
            </button>
          ))
        )}
      </div>

      {term.length >= MIN_CHARS && (
        <button onClick={openList} data-testid="global-search-all"
          className="flex w-full items-center gap-2 border-t border-[#ebebeb] px-4 py-2.5 text-[13px] font-medium text-[#005bd3] transition-colors hover:bg-[#f7f8fa]">
          <ListFilter size={14} /> Voir tous les résultats pour « {term} »
        </button>
      )}

      <div className="flex items-center gap-3.5 border-t border-[#ebebeb] bg-[#fafafa] px-3.5 py-2.5 text-[11px] text-[#94a3b8]">
        <span className="flex items-center gap-1"><CornerDownLeft size={11} /> ouvrir</span>
        <span>↑ ↓ naviguer</span>
        <span>Échap fermer</span>
      </div>
    </Modal>
  );
}
